import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import {
  computePercentage,
  formatMonetaryValue,
  formatNumber,
  formatPercentage,
} from '@ydistri/generic-utils';
import { DataTable, DataTableDivider, Section, tableScroll } from '@ydistri/ds';
import { computeRemSize } from '@ydistri/theme';
import { useSelector } from 'react-redux';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import { ReduxState } from '../../../../store';
import { useGetStatisticsQuery } from '../apiCalculationStatistics';

interface StatisticDimensionApiItem {
  valueSource: number;
  quantitySource: number;
  valueTarget: number;
  quantityTarget: number;
}

interface StatisticDimensionStat {
  title: string;
  key: string;
  valueSource: number;
  valueSourcePercentage: number;
  quantitySource: number;
  quantitySourcePercentage: number;
  valueTarget: number;
  valueTargetPercentage: number;
  quantityTarget: number;
  quantityTargetPercentage: number;
}

type StatisticDimensionStatistics = StatisticDimensionStat | DataTableDivider;

const STATISTIC_DIMENSION_VALUE_COLUMN_WIDTH = computeRemSize(220);
const STATISTIC_DIMENSION_QUANTITY_COLUMN_WIDTH = computeRemSize(200);
const STATISTIC_DIMENSION_PERCENTAGE_COLUMN_WIDTH = computeRemSize(60);

interface StatisticDimensionSectionProps<T extends StatisticDimensionApiItem> {
  title: string;
  tableId: string;
  dimensionColumnTitle: string;
  data?: T[];
  loading: boolean;
  getItemTitle: (item: T) => string;
  getItemKey: (item: T) => string;
}

const getStatisticDimensionColumns = (
  currencyId: string,
  dimensionColumnTitle: string,
): ColumnsType<StatisticDimensionStatistics> => [
  {
    title: dimensionColumnTitle,
    dataIndex: 'title',
    key: 'title',
    sorter: (a, b) => {
      if ('title' in a && 'title' in b) return a.title.localeCompare(b.title);
      return 0;
    },
  },
  {
    title: 'Source value',
    dataIndex: 'valueSource',
    key: 'valueSource',
    align: 'right',
    width: STATISTIC_DIMENSION_VALUE_COLUMN_WIDTH,
    render: (valueSource: number) => formatMonetaryValue(currencyId, valueSource),
    defaultSortOrder: 'descend',
    sorter: (a, b) =>
      'valueSource' in a && 'valueSource' in b ? a.valueSource - b.valueSource : 0,
  },
  {
    title: '',
    dataIndex: 'valueSourcePercentage',
    key: 'valueSourcePercentage',
    align: 'right',
    width: STATISTIC_DIMENSION_PERCENTAGE_COLUMN_WIDTH,
    render: (valueSourcePercentage: number) => `(${formatPercentage(valueSourcePercentage)})`,
  },
  {
    title: 'Source quantity',
    dataIndex: 'quantitySource',
    key: 'quantitySource',
    align: 'right',
    width: STATISTIC_DIMENSION_QUANTITY_COLUMN_WIDTH,
    render: (quantitySource: number) => `${formatNumber(quantitySource)} MU`,
    sorter: (a, b) =>
      'quantitySource' in a && 'quantitySource' in b ? a.quantitySource - b.quantitySource : 0,
  },
  {
    title: '',
    dataIndex: 'quantitySourcePercentage',
    key: 'quantitySourcePercentage',
    align: 'right',
    width: STATISTIC_DIMENSION_PERCENTAGE_COLUMN_WIDTH,
    render: (quantitySourcePercentage: number) => `(${formatPercentage(quantitySourcePercentage)})`,
  },
  {
    title: 'Target value',
    dataIndex: 'valueTarget',
    key: 'valueTarget',
    align: 'right',
    width: STATISTIC_DIMENSION_VALUE_COLUMN_WIDTH,
    render: (valueTarget: number) => formatMonetaryValue(currencyId, valueTarget),
    sorter: (a, b) =>
      'valueTarget' in a && 'valueTarget' in b ? a.valueTarget - b.valueTarget : 0,
  },
  {
    title: '',
    dataIndex: 'valueTargetPercentage',
    key: 'valueTargetPercentage',
    align: 'right',
    width: STATISTIC_DIMENSION_PERCENTAGE_COLUMN_WIDTH,
    render: (valueTargetPercentage: number) => `(${formatPercentage(valueTargetPercentage)})`,
  },
  {
    title: 'Target quantity',
    dataIndex: 'quantityTarget',
    key: 'quantityTarget',
    align: 'right',
    width: STATISTIC_DIMENSION_QUANTITY_COLUMN_WIDTH,
    render: (quantityTarget: number) => `${formatNumber(quantityTarget)} MU`,
    sorter: (a, b) =>
      'quantityTarget' in a && 'quantityTarget' in b ? a.quantityTarget - b.quantityTarget : 0,
  },
  {
    title: '',
    dataIndex: 'quantityTargetPercentage',
    key: 'quantityTargetPercentage',
    align: 'right',
    width: STATISTIC_DIMENSION_PERCENTAGE_COLUMN_WIDTH,
    render: (quantityTargetPercentage: number) => `(${formatPercentage(quantityTargetPercentage)})`,
  },
];

const StatisticDimensionSection = <T extends StatisticDimensionApiItem>({
  title,
  tableId,
  dimensionColumnTitle,
  data,
  loading,
  getItemTitle,
  getItemKey,
}: StatisticDimensionSectionProps<T>): React.ReactElement => {
  const templateOrCalculation = useTemplateOrCalculation();
  const currencyId = useCurrency();
  const departmentId = useSelector((state: ReduxState) => state.statistics.departmentId);
  const clusterType = useSelector((state: ReduxState) => state.statistics.clusterType);

  const { data: overallStatisticsData, isFetching: isFetchingStatistics } = useGetStatisticsQuery({
    calculationId: templateOrCalculation.id,
    departmentId,
    clusterType,
  });

  const columns: ColumnsType<StatisticDimensionStatistics> = useMemo(
    () => getStatisticDimensionColumns(currencyId, dimensionColumnTitle),
    [currencyId, dimensionColumnTitle],
  );

  const tableData: StatisticDimensionStatistics[] = useMemo(() => {
    if (!data || !overallStatisticsData) return [];

    const quantity = overallStatisticsData.quantity;
    const value = overallStatisticsData.value;

    return data.map(item => ({
      title: getItemTitle(item),
      key: getItemKey(item),
      valueSource: item.valueSource,
      quantitySource: item.quantitySource,
      valueTarget: item.valueTarget,
      quantityTarget: item.quantityTarget,
      valueSourcePercentage: computePercentage(item.valueSource, value),
      quantitySourcePercentage: computePercentage(item.quantitySource, quantity),
      valueTargetPercentage: computePercentage(item.valueTarget, value),
      quantityTargetPercentage: computePercentage(item.quantityTarget, quantity),
    }));
  }, [data, getItemKey, getItemTitle, overallStatisticsData]);

  return (
    <Section header={title}>
      <DataTable<StatisticDimensionStatistics>
        id={tableId}
        rowKey="key"
        dataSource={tableData}
        columns={columns}
        loading={loading || isFetchingStatistics}
        showHeader={true}
        tableLayout="fixed"
        scroll={tableScroll}
        height="200px"
      />
    </Section>
  );
};

export default StatisticDimensionSection;
